import { NO_SLA, type SlaSnapshot } from "@helpdesk/sla";

import type { SalesCall } from "./sales-types";

/**
 * A sales call's overdue rules, in the shape the ticket SLA clock already
 * speaks.
 *
 * Jade asked for "the same little clock as tickets", and the cheapest way to
 * actually get the same one is to hand the same component the same type rather
 * than to draw a second dial that drifts from the first. So this is a
 * translation, not a new widget: `SlaClock` and `SlaIndicator` are unchanged.
 *
 * ## Where the numbers come from
 *
 * The server computes `attention.dueBy` -- the soonest deadline any rule
 * imposes, whether that is the group's no-attempt hours, its stale days, a
 * days-since-raised rule or a date somebody put on the call by hand. Which
 * rule produced it does not matter here; the guarantee is that `overdue` flips
 * exactly when `dueBy` passes.
 *
 * The window it is measured against starts at the last attempt, or at the
 * moment the call was raised when nobody has rung yet -- mirroring how the
 * server picks its threshold. That is what fills the dial; without it the
 * clock could say "2h left" but not whether that is two hours out of three or
 * out of three weeks.
 *
 * ## Two honest differences from a ticket's SLA
 *
 * A ticket's clock is measured in *business* minutes and freezes overnight.
 * This one is wall-clock: the sales rules are written in plain hours and days
 * ("chase after 3 days"), and pretending otherwise would make a call raised on
 * Friday look due on Monday when the rule says Sunday. So `paused` is always
 * false -- the clock here never freezes, and nothing should dim it.
 *
 * A waiting call -- somebody asked to be rung back at four -- counts down to
 * the callback rather than to a threshold, which is why it is not treated as
 * "nothing to measure" even though it is never overdue.
 */
export function salesSla(call: SalesCall): SlaSnapshot {
  const dueBy = call.attention.dueBy;
  /* No rules configured for the group, or the call is closed. The clock
     renders its dashed "not measured" face, exactly as an untimed pipeline
     does on a ticket. */
  if (!dueBy) return NO_SLA;

  const due = new Date(dueBy).getTime();
  if (Number.isNaN(due)) return NO_SLA;

  const now = Date.now();
  const startedAt = new Date(call.lastAttemptAt ?? call.createdAt).getTime();

  const remainingMinutes = Math.round((due - now) / 60_000);
  const targetMinutes = Math.max(1, Math.round((due - startedAt) / 60_000));
  const elapsedMinutes = Math.round((now - startedAt) / 60_000);

  return {
    level: level(remainingMinutes, targetMinutes),
    targetMinutes,
    elapsedMinutes,
    remainingMinutes,
    /* Clamped, because the dial cannot draw more than a full circle and a call
       three weeks over would otherwise wrap round to looking nearly fresh. */
    consumed: Math.min(1, Math.max(0, elapsedMinutes / targetMinutes)),
    dueAt: new Date(due).toISOString(),
    running: true,
    paused: false,
    /* Never frozen, so never for a reason. A sales call is not assigned an
       SLA that anybody's lunch bends -- the rules are wall-clock days, and a
       rep at lunch does not move a callback booked for four o'clock. */
    pausedReason: null,
    pausedMinutes: 0,
  };
}

/**
 * Green, amber, red.
 *
 * The amber band is a *proportion* rather than the ticket layer's fixed
 * fifteen minutes, because these windows are days long: fifteen minutes'
 * warning on a three-day rule is no warning at all. A quarter of the allowance
 * left is the point at which chasing is still realistic.
 */
function level(
  remainingMinutes: number,
  targetMinutes: number,
): SlaSnapshot["level"] {
  if (remainingMinutes < 0) return "breached";
  return remainingMinutes <= targetMinutes * 0.25 ? "warning" : "ok";
}
